import type { GalleryPhoto, Photo } from '@capacitor/camera'
import { Input, Modal, Select, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { ErrorBoundary } from '@sentry/react'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { FIXED_CHILDREN_MODAL_TRANSITION } from 'components/AboveAllModalOverlay/fixedChildrenModalTransition'
import { Button } from 'components/Button/Button'
import { IconCameraFill } from 'components/icons/IconCameraFill'
import { WideHeader } from 'components/ModalDrawer/WideHeader/WideHeader'
import { translate } from 'i18n/i18n'
import type { PropsWithChildren } from 'react'
import React, { useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { checkAuthorization } from 'utils/checkAuthorization'
import { ACTION_MENU_MODAL_ID } from '../ActionMenu/ActionMenu'
import { ImageUpload } from '../ImageUpload/ImageUpload'
import { NodeIdentifierField } from '../NodeInstallation/EquipmentActivation/NodeIdentifierField/NodeIdentifierField'
import { Footer } from '../NodeInstallation/Footer/Footer'
import { NodeInfoHeader } from '../Shared/NodeInfoHeader/NodeInfoHeader'
import { serviceCenterStore } from '../store/serviceCenterStore'
import type { TActiveNode } from '../types'
import { createErrorModal } from '../utils/createCommonModals'
import { swapNode } from '../utils/swapNode'
import { uploadInstallationImage } from '../utils/uploadInstallationImage'
import { isValidNodeIdentifier } from '../utils/validateNodeIdentifier'

const MODAL_ID = 'node-swap-modal'

export const openNodeSwapModal = (node?: TActiveNode, onClose?: () => void) => {
  AboveAllModal.open({
    modalId: MODAL_ID,
    fullScreen: true,
    withCloseButton: false,
    padding: 0,
    transitionProps: FIXED_CHILDREN_MODAL_TRANSITION,
    children: (
      <ErrorBoundary>
        <NodeSwapModal
          node={node}
          onClose={() => {
            onClose && onClose()

            AboveAllModal.close(MODAL_ID)
          }}
        />
      </ErrorBoundary>
    ),
    styles: {
      content: {
        marginLeft: 'env(safe-area-inset-left)',
        marginRight: 'env(safe-area-inset-right)',
        boxShadow: 'none',
      },
    },
  })
}

export const NodeSwapModal: React.FC<{ onClose: () => void; node?: TActiveNode }> = ({ onClose, node }) => {
  const [isModalOpenedImage, setIsModalOpenedImage] = useState(false)
  const [image, setImage] = useState<Photo | GalleryPhoto>()
  const [isSaving, setIsSaving] = useState(false)

  const mapSelectedNode = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getSelectedEquipmentNode,
  ) as TActiveNode

  const selectedNode = mapSelectedNode || node

  const swapReasons = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getSwapReasonsBySelectedNodeType(s, selectedNode.nodeType),
  )

  // user click from node card -> action menu -> mapSelectedNode should exist
  // user click from status tab -> action menu -> node is provided

  const form = useForm({
    initialValues: {
      swapReason: '',
      newName: selectedNode?.name || '',
      nodeIdentifierToSwapIn: '',
      reasonDescription: '',
    },
    validate: {
      swapReason: (value) => {
        if (!value) return translate.phrases.placeholder('Required')
      },
      nodeIdentifierToSwapIn: (value) => {
        if (!value) return translate.phrases.placeholder('Required')

        if (!isValidNodeIdentifier(value.trim().toUpperCase(), selectedNode.nodeType)) {
          return translate.phrases.placeholder(
            'The node identifier that you have entered is invalid. Please try again.',
          )
        }
      },
      reasonDescription: (value) => {
        if (form.values.swapReason === 'OTHER' && !value)
          return translate.phrases.placeholder('Please provide a reason description when selecting "Other"')
      },
    },
    transformValues(values) {
      return {
        ...values,
        nodeIdentifierToSwapIn: values.nodeIdentifierToSwapIn.trim().toUpperCase(),
      }
    },
  })

  const handleSubmit = async () => {
    const { hasErrors } = form.validate()

    if (hasErrors) return

    setIsSaving(true)

    const { newName, nodeIdentifierToSwapIn, swapReason, reasonDescription } = form.values

    try {
      const payload = {
        nodeIdentifierToRemove: selectedNode?.nodeIdentifier,
        nodeIdentifierToInstall: nodeIdentifierToSwapIn,
        reason: swapReason,
        newName,
        reasonDescription: reasonDescription || undefined,
        propertyId: selectedNode?.propertyId,
      }

      await swapNode(payload)

      if (image) {
        await uploadInstallationImage(nodeIdentifierToSwapIn, image, 'EQUIP_SWAP_IN')
      }

      notifications.show({
        title: translate.phrases.placeholder('Swap Node'),
        message: translate.phrases.placeholder('Node has been swapped successfully'),
      })

      serviceCenterStore.actions.selectEquipment(nodeIdentifierToSwapIn, 'active')

      AboveAllModal.close(ACTION_MENU_MODAL_ID)

      onClose()
    } catch (e) {
      AboveAllModal.open(createErrorModal(translate.phrases.placeholder('Error'), (e as Error).message))
    }

    setIsSaving(false)
  }

  const openImageModal = () => {
    setIsModalOpenedImage(true)
  }

  const IconWrapper: React.FC<PropsWithChildren> = ({ children }) => {
    return (
      <span
        css={{
          fontSize: '20px',
          display: 'inline-block',
          marginLeft: '4px',
          position: 'relative',
          top: '4px',
        }}
      >
        {children}
      </span>
    )
  }

  return (
    <>
      {isModalOpenedImage && (
        <Modal
          zIndex={SharedSettings.DEFAULT_CAMERA_Z_INDEX - 1}
          onClose={() => setIsModalOpenedImage(false)}
          opened={isModalOpenedImage}
        >
          <ImageUpload onlyCamera image={image} setImage={setImage} />
        </Modal>
      )}
      <WideHeader onClose={onClose} title={translate.phrases.placeholder('Swap Equipment')} />

      <div css={{ padding: '16px' }}>
        <NodeInfoHeader node={selectedNode} />

        <Input.Wrapper
          id="newName"
          label={translate.phrases.placeholder('Equipment Name')}
          css={{ marginTop: 20 }}
        >
          <TextInput
            placeholder={translate.phrases.placeholder('Insert New Equipment Name')}
            css={{ margin: '10px auto', display: 'block' }}
            {...form.getInputProps('newName')}
          />
        </Input.Wrapper>
        <NodeIdentifierField
          required
          label={translate.phrases.placeholder('Node Identifier')}
          placeholder={translate.phrases.placeholder('Type or Scan ID')}
          {...form.getInputProps('nodeIdentifierToSwapIn')}
          onChange={(e) => {
            //Override default onChanged in getInputProps to handle uppercase
            form.setValues((prev) => ({
              ...prev,
              nodeIdentifierToSwapIn: e.target.value.toUpperCase(),
            }))
          }}
        />

        <Input.Wrapper id="reason" label={translate.phrases.placeholder('Reason')} css={{ marginTop: 20 }}>
          <Select
            data={swapReasons.map((item) => ({ label: item.swapReason, value: item.swapId }))}
            {...form.getInputProps('swapReason')}
            styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
            placeholder={translate.phrases.placeholder('Select Reason for Swap')}
          />
        </Input.Wrapper>

        {form.values.swapReason === 'OTHER' && (
          <Input.Wrapper
            id="reasonDescription"
            label={translate.phrases.placeholder('Description')}
            css={{ marginTop: 20 }}
          >
            <TextInput
              css={{ margin: '10px auto', display: 'block' }}
              {...form.getInputProps('reasonDescription')}
            />
          </Input.Wrapper>
        )}
        {checkAuthorization({ permission: 'CREATE_SSC_NODE_IMAGE', entity: selectedNode.propertyId }) && (
          <Button
            onClick={openImageModal}
            variant="tertiary"
            css={{
              width: '100%',
              height: '40px',
              marginTop: '16px',
            }}
          >
            <span>{translate.phrases.placeholder(image ? 'Change Image' : 'Add Image')}</span>
            <IconWrapper>
              <IconCameraFill />
            </IconWrapper>
          </Button>
        )}
      </div>
      <Footer
        onPrevious={onClose}
        onNext={handleSubmit}
        nextButtonLabel={translate.phrases.placeholder('Submit')}
        disableNextButton={isSaving}
      />
    </>
  )
}
